import { PublicPageZip } from "@/utils/page";
import { JobGetResponse } from "@/types/job";
import Section from "@/components/Section/Section";
import SectionContainer from "@/components/Section/SectionContainer";
import classNames from "classnames";
import Link from "@/components/UI/Link/Link";
import Image from "next/image";
import Button from "@/components/UI/Button/Button";
import MiniJobCreator from "@/components/UI/MiniJobCreator/MiniJobCreator";

interface Section19Props extends React.HTMLAttributes<HTMLElement> {
  job?: JobGetResponse;
  page: any;
  section: any;
}

const HeroSection = ({ job, page, section, ...props }: Section19Props) => {
  return (
    <Section className="bg-vpBlue-200" section={section} {...props}>
      <SectionContainer className={classNames("relative", "pb-0")} section={section}>
        <div className="relative z-10 flex flex-col">
          <div className="flex flex-1 flex-col gap-14 pb-20">
            <div className="flex flex-col gap-8">
              {section?.Overline && (
                <div className={classNames("text-5xl font-bold lg:w-2/3")}>{section?.Overline}</div>
              )}
              {section?.Title && (
                <h1
                  className={classNames("text-5xl font-bold lg:w-2/3", section?.TitleElement?.ClassName)}
                  id={section?.TitleElement?.HtmlId}
                  style={section?.TitleElement?.Style}
                  {...section?.TitleElement?.Rest}
                >
                  {section?.Title}
                </h1>
              )}
              {section?.Subtitle && (
                <h2
                  className={classNames("text-xl text-vpGray-700 lg:w-2/3", section?.SubtitleElement?.ClassName)}
                  id={section?.SubtitleElement?.HtmlId}
                  style={section?.SubtitleElement?.Style}
                  {...section?.SubtitleElement?.Rest}
                >
                  {section?.Subtitle}
                </h2>
              )}
              {section?.Button && (
                <Link className="block" href={section?.Button?.Url ?? ""} variant="external">
                  <Button
                    size="large"
                    startNode={
                      section?.Button?.StartNode?.data?.attributes && (
                        <div
                          className={classNames("h-5 w-5", section?.Button?.StartNodeClassName)}
                          style={{
                            mask: `url(${section?.Button?.StartNode?.data?.attributes?.url}) no-repeat center / contain`,
                            WebkitMask: `url(${section?.Button?.StartNode?.data?.attributes?.url}) no-repeat center / contain`,
                          }}
                        />
                      )
                    }
                  >
                    {section?.Button?.Value}
                  </Button>
                </Link>
              )}
              {section?.MiniJobCreator && (
                <MiniJobCreator
                  buttonText={section?.MiniJobCreator?.buttonText ?? ""}
                  fieldPlaceholder={section?.MiniJobCreator?.placeholderText ?? ""}
                />
              )}
            </div>
          </div>
          {section?.Boxes.length > 1 && (
            <div className={classNames("hidden divide-x bg-white py-10 lg:flex xl:py-12")}>
              {section?.Boxes.map((box: any) => {
                return (
                  <div className="flex flex-1 gap-4 px-5 xl:px-10" key={box.id}>
                    {box.Icon?.data?.attributes && (
                      <div
                        className={classNames("h-6 w-6 shrink-0", box?.IconClassName)}
                        style={{
                          mask: `url(${box.Icon?.data?.attributes?.url}) no-repeat center / contain`,
                          WebkitMask: `url(${box.Icon?.data?.attributes?.url}) no-repeat center / contain`,
                          ...box?.IconStyle,
                        }}
                      />
                    )}
                    <div className="flex flex-col gap-2" key={box?.id}>
                      <Link className="text-lg font-bold" href={box?.LinkUrl ?? ""}>
                        {box?.LinkValue}
                      </Link>
                      <div className="text-vpGray-700">{box?.Body}</div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
        {section?.Image?.data?.attributes && (
          <Image
            alt={section?.Image?.data?.attributes?.alternativeText}
            className={classNames("absolute z-10 hidden xl:block", section?.ImageElement?.ClassName)}
            id={section?.ImageElement?.HtmlId}
            src={section?.Image?.data?.attributes?.url}
            style={section?.ImageElement?.Style}
            width={section?.Image?.data?.attributes?.width ?? "100"}
            height={section?.Image?.data?.attributes?.height ?? "100"}
            priority
            {...section?.ImageElement?.Rest}
          />
        )}
      </SectionContainer>
    </Section>
  );
};

export default HeroSection;
